import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodFormResolver as zodResolver } from "@/lib/zodFormResolver";
import * as z from "zod";
import { formatDistanceToNow } from "date-fns";
import {
  useGetMe,
  useGetBrandSettings,
  useGetSenderAddress,
  useUpdateBrandSettings,
  useHealthCheck,
  useListInboundMailboxes,
  useCreateInboundMailbox,
  useUpdateInboundMailbox,
  useDeleteInboundMailbox,
  useRestoreInboundMailbox,
  useTestInboundWebhook,
  useAdminPurgeInboundMailbox,
  useListTrustedSenders,
  useCreateTrustedSender,
  useDeleteTrustedSender,
  getGetBrandSettingsQueryKey,
  getGetSenderAddressQueryKey,
  getGetSenderDnsRecordsQueryKey,
  getHealthCheckQueryKey,
  getListInboundMailboxesQueryKey,
  getListTrustedSendersQueryKey,
  type UpdateBrandSettingsInput,
  type InboundMailbox,
  type InboundWebhookDiagnostics,
  type TestInboundWebhookResult,
  type TrustedSender,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { DMARC_POLICY_INPUT_VALUES, DMARC_POLICY_LABELS } from "@/lib/enumValues";
import { MySendingDefaultsCard } from "@/components/MySendingDefaultsCard";
import { GmailPreferencesCard } from "@/components/GmailPreferencesCard";
import { WebhookPreferencesCard } from "@/components/WebhookPreferencesCard";
import { Button } from "@/components/ui/button";
import {
  Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  Palette,
  AtSign,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Gauge,
  Inbox as InboxIcon,
  Clock,
  Plus,
  Pencil,
  Trash2,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  Archive,
  Send,
  ShieldAlert,
  CircleHelp,
  Lock,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import DeliverabilityTips from "@/components/DeliverabilityTips";
import { normalizeTrustedDomain } from "@/lib/trustedSenders";
import { BadgeCheck } from "lucide-react";
import {
  RecheckSenderButton,
  type RecheckCooldownState,
} from "@/components/RecheckSenderButton";
import { SenderStatusIndicator } from "@/components/SenderStatusIndicator";
import { DnsRecordsCard } from "@/components/DnsRecordsCard";

const LOCAL_PART_PATTERN = /^[A-Za-z0-9._+-]+$/;
const LOCAL_PART_MAX_LENGTH = 64;
const DISPLAY_NAME_MAX_LENGTH = 120;

type SampleLogo = { name: string; svgUrl: string; pngUrl: string };

const SAMPLE_LOGOS: SampleLogo[] = [
  {
    name: "Apple",
    svgUrl: "https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg",
    pngUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/Apple_logo_black.svg/640px-Apple_logo_black.svg.png",
  },
  {
    name: "Microsoft",
    svgUrl: "https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg",
    pngUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/44/Microsoft_logo.svg/640px-Microsoft_logo.svg.png",
  },
  {
    name: "PayPal",
    svgUrl: "https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg",
    pngUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/PayPal.svg/640px-PayPal.svg.png",
  },
];

const SAMPLE_LOGO_ROWS: { format: "svg" | "png"; label: string; helper: string }[] = [
  {
    format: "svg",
    label: "SVG",
    helper: "Sharp at any size (best for in-app preview).",
  },
  {
    format: "png",
    label: "PNG",
    helper: "Renders reliably in Gmail, Outlook, and other email clients.",
  },
];

// Mirrors the server's local-part regex (LOCAL_PART_RE in resend.ts) so the
// UI gives the user the same validation feedback the API would. Keep these
// in lockstep — diverging will cause confusing "saved but reverted" UX.
const LOCAL_PART_RE = /^[a-z0-9](?:[a-z0-9._-]{0,62}[a-z0-9])?$/;
const FROM_PRESETS: readonly string[] = [
  "john",
  "peter",
  "michelle",
  "jessica",
  "edward",
  "harry",
];

const formSchema = z.object({
  companyName: z.string().min(1, "Company Name is required").max(120),
  fromName: z.string().max(120).optional().nullable(),
  fromLocalPart: z
    .string()
    .max(64)
    .optional()
    .nullable()
    .refine(
      (v) => !v || LOCAL_PART_RE.test(v),
      "Use 1-64 lowercase letters, digits, '.', '_' or '-'. Must start and end with a letter or digit.",
    ),
  logoUrl: z.string().url("Must be a valid URL").optional().nullable().or(z.literal("")),
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{3,8}$/, "Must be a valid hex color"),
  accentColor: z.string().regex(/^#[0-9A-Fa-f]{3,8}$/, "Must be a valid hex color"),
  websiteUrl: z.string().url("Must be a valid URL").optional().nullable().or(z.literal("")),
  supportEmail: z.string().email("Must be a valid email").optional().nullable().or(z.literal("")),
  addressLine: z.string().optional().nullable(),
  footerNote: z.string().optional().nullable(),
  unsubscribeUrl: z.string().url("Must be a valid URL").optional().nullable().or(z.literal("")),
  trackOpens: z.boolean(),
  trackClicks: z.boolean(),
  dmarcPolicy: z.enum(DMARC_POLICY_INPUT_VALUES),
  dmarcReportingEmail: z
    .string()
    .max(254, "Email is too long")
    .email("Must be a valid email")
    .optional()
    .nullable()
    .or(z.literal("")),
});

type InboxScoreCheck = {
  key: string;
  label: string;
  ok: boolean;
  helper: string;
  critical?: boolean;
};

type InboxScoreTier = "excellent" | "good" | "needs-work" | "critical" | "loading";

function tierMeta(tier: InboxScoreTier): {
  label: string;
  badgeClass: string;
  ringClass: string;
} {
  switch (tier) {
    case "excellent":
      return {
        label: "Excellent",
        badgeClass:
          "bg-green-500/10 text-green-700 border-green-500/30 dark:text-green-400",
        ringClass: "text-green-600 dark:text-green-400",
      };
    case "good":
      return {
        label: "Good",
        badgeClass:
          "bg-amber-500/10 text-amber-700 border-amber-500/30 dark:text-amber-400",
        ringClass: "text-amber-600 dark:text-amber-400",
      };
    case "needs-work":
      return {
        label: "Needs work",
        badgeClass:
          "bg-orange-500/10 text-orange-700 border-orange-500/30 dark:text-orange-400",
        ringClass: "text-orange-600 dark:text-orange-400",
      };
    case "critical":
      return {
        label: "Action required",
        badgeClass:
          "bg-red-500/10 text-red-700 border-red-500/30 dark:text-red-400",
        ringClass: "text-red-600 dark:text-red-400",
      };
    case "loading":
    default:
      return {
        label: "Checking…",
        badgeClass:
          "bg-muted text-muted-foreground border-border",
        ringClass: "text-muted-foreground",
      };
  }
}

export default function BrandSettings() {
  const queryClient = useQueryClient();
  const { data: me } = useGetMe();
  const { data: settings, isLoading: isBrandLoading } = useGetBrandSettings();
  const { data: sender, isLoading: isSenderLoading } = useGetSenderAddress();
  const isLoading = isBrandLoading || isSenderLoading;
  const updateMutation = useUpdateBrandSettings();
  const healthQuery = useHealthCheck({
    query: {
      queryKey: getHealthCheckQueryKey(),
      staleTime: 5 * 60 * 1000,
    },
  });
  const senderConfig = healthQuery.data?.senderConfig;
  const senderStatus = senderConfig?.status;
  const senderReason = senderConfig?.reason;
  // Declared up here (above the isLoading early-return) because hooks must be
  // called in the same order on every render. Previously this useState lived
  // further down the function body, which meant the first render skipped it
  // (early-returned a spinner) and the next render called it, triggering
  // React's "Rendered more hooks than during the previous render" crash and
  // breaking the Inbox Score banner UI.
  const [senderRecheckCooldown, setSenderRecheckCooldown] =
    useState<RecheckCooldownState | null>(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyName: "",
      fromName: "",
      fromLocalPart: "",
      logoUrl: "",
      primaryColor: "#2563EB",
      accentColor: "#F8FAFC",
      websiteUrl: "",
      supportEmail: "",
      addressLine: "",
      footerNote: "",
      unsubscribeUrl: "",
      trackOpens: false,
      trackClicks: false,
      dmarcPolicy: "none",
      dmarcReportingEmail: "",
    },
  });

  useEffect(() => {
    if (settings) {
      form.reset({
        companyName: settings.companyName,
        fromName: settings.fromName || "",
        fromLocalPart: settings.fromLocalPart || "",
        logoUrl: settings.logoUrl || "",
        primaryColor: settings.primaryColor,
        accentColor: settings.accentColor,
        websiteUrl: settings.websiteUrl || "",
        supportEmail: settings.supportEmail || "",
        addressLine: settings.addressLine || "",
        footerNote: settings.footerNote || "",
        unsubscribeUrl: settings.unsubscribeUrl || "",
        trackOpens: settings.trackOpens ?? false,
        trackClicks: settings.trackClicks ?? false,
        dmarcPolicy: settings.dmarcPolicy ?? "none",
        dmarcReportingEmail: settings.dmarcReportingEmail || "",
      });
    }
  }, [settings, form]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const data: UpdateBrandSettingsInput = {
        companyName: values.companyName,
        fromName: values.fromName?.trim() ? values.fromName.trim() : null,
        fromLocalPart: values.fromLocalPart?.trim()
          ? values.fromLocalPart.trim().toLowerCase()
          : null,
        logoUrl: values.logoUrl || null,
        primaryColor: values.primaryColor,
        accentColor: values.accentColor,
        websiteUrl: values.websiteUrl || null,
        supportEmail: values.supportEmail || null,
        addressLine: values.addressLine || null,
        footerNote: values.footerNote || null,
        unsubscribeUrl: values.unsubscribeUrl || null,
        trackOpens: values.trackOpens,
        trackClicks: values.trackClicks,
        dmarcPolicy: values.dmarcPolicy,
        dmarcReportingEmail: values.dmarcReportingEmail?.trim()
          ? values.dmarcReportingEmail.trim()
          : null,
      };
      await updateMutation.mutateAsync({ data });
      toast.success("Brand settings updated");
      queryClient.invalidateQueries({ queryKey: getGetBrandSettingsQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetSenderAddressQueryKey() });
      // The synthesized DMARC record reads from these saved fields, so
      // re-fetch the DNS records card to reflect the new policy / rua=
      // value on the next render rather than after the cache stales out.
      queryClient.invalidateQueries({
        queryKey: getGetSenderDnsRecordsQueryKey(),
      });
    } catch (e: unknown) {
      toast.error(apiErrorMessage(e, "Failed to update brand settings"));
    }
  };

  if (isLoading) {
    return <div className="flex-1 flex justify-center items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const fromEmail = sender?.fromEmail ?? "";
  // The verified shared sending domain — pinned by the API. We render it as
  // the read-only "@<domain>" suffix next to the local-part input. Falls
  // back to slicing the legacy fromEmail if /brand has not yet returned the
  // explicit fromDomain field (older clients during a deploy).
  const fromDomain =
    settings?.fromDomain ||
    (fromEmail.includes("@") ? fromEmail.split("@")[1] : "");
  // The verified shared local-part (e.g. "support") — used as the placeholder
  // and as the fallback shown in the Preview when the user hasn't picked a
  // custom local-part yet.
  const defaultLocalPart =
    fromEmail.includes("@") ? fromEmail.split("@")[0] : "";
  const fromNameWatch = form.watch("fromName") || "";
  const fromLocalPartWatch = form.watch("fromLocalPart") || "";
  const supportEmailWatch = form.watch("supportEmail") || "";
  const unsubscribeUrlWatch = form.watch("unsubscribeUrl") || "";
  const addressLineWatch = form.watch("addressLine") || "";
  // Preview mirrors the server's fromAddress() builder: invalid local-parts
  // fall back to the verified shared local-part rather than rendering as-is,
  // so the user sees exactly what recipients will see.
  const effectiveLocalPart =
    fromLocalPartWatch.trim() && LOCAL_PART_RE.test(fromLocalPartWatch.trim().toLowerCase())
      ? fromLocalPartWatch.trim().toLowerCase()
      : defaultLocalPart;
  // "Support Team" is the friendly default — matches the schema default
  // and the server-side fallback in `fromAddress()` so the preview always
  // matches what recipients will see.
  const previewFrom = fromNameWatch.trim()
    ? `${fromNameWatch.trim()} <${effectiveLocalPart}@${fromDomain}>`
    : `Support Team <${effectiveLocalPart}@${fromDomain}>`;

  const senderUnverified = senderStatus === "unverified";
  const senderUnknown = senderStatus === "unknown" || !senderStatus;
  // Workspace brand is force-imposed by the source admin: every
  // non-admin user (and even other admins) sees the admin's brand
  // and is blocked from editing it. Mirror that on the client by
  // disabling the form and surfacing a banner that names the admin.
  // The PUT is also gated server-side, so even if a stale client
  // tries to submit, the toast falls back to the API's 403 message.
  const isManaged = !!settings?.isManaged;
  const managedByEmail = settings?.managedByEmail ?? null;
  // Inbox Score covers ONLY items that actually affect inbox placement /
  // anti-spam compliance. Brand polish (logo, colors) is configured below
  // but intentionally not scored, so 100% is achievable with the fields
  // recipients' mail providers care about.
  const inboxChecks: InboxScoreCheck[] = [
    {
      key: "verified-sender",
      label: "Sender domain verified for sending",
      ok: senderStatus === "verified",
      critical: true,
      helper:
        senderStatus === "verified"
          ? "Your shared MailCraft sender is authenticated (SPF, DKIM, DMARC) and ready to deliver."
          : senderUnverified
            ? `MAILCRAFT_FROM_EMAIL is not a verified Resend sender — sends will be rejected.${senderReason ? ` ${senderReason}` : ""}`
            : "Verification status not yet known — try again in a moment.",
    },
    {
      key: "from-name",
      label: "Friendly From name set",
      ok: !!fromNameWatch.trim(),
      helper:
        "Recipients see your brand in their inbox instead of just an email address.",
    },
    {
      key: "support-email",
      label: "Support email set",
      ok: !!supportEmailWatch.trim(),
      helper:
        "Used in the footer and in the List-Unsubscribe mailto: header that mail clients honor.",
    },
    {
      key: "unsubscribe-url",
      label: "One-click unsubscribe URL set",
      ok: !!unsubscribeUrlWatch.trim(),
      helper:
        "Required by Gmail and Yahoo for high-volume senders. Powers the List-Unsubscribe https header.",
    },
    {
      key: "company-address",
      label: "Company / mailing address set",
      ok: !!addressLineWatch.trim(),
      helper:
        "Anti-spam laws (CAN-SPAM and similar) require a physical postal address in commercial email.",
    },
  ];

  const passingChecks = inboxChecks.filter((c) => c.ok).length;
  const totalChecks = inboxChecks.length;
  const percent = Math.round((passingChecks / totalChecks) * 100);

  let inboxTier: InboxScoreTier;
  if (healthQuery.isLoading) {
    inboxTier = "loading";
  } else if (senderUnverified) {
    inboxTier = "critical";
  } else if (passingChecks === totalChecks) {
    inboxTier = "excellent";
  } else if (percent >= 67) {
    inboxTier = "good";
  } else {
    inboxTier = "needs-work";
  }
  const tier = tierMeta(inboxTier);

  return (
    <div className="flex-1 overflow-auto p-8 bg-background">
      <div className="max-w-3xl mx-auto space-y-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Brand Settings</h1>
          <p className="text-muted-foreground">Configure the default branding injected into your templates.</p>
        </div>

        <Card data-testid="card-inbox-score">
          <CardHeader>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Gauge className="w-5 h-5 text-primary" /> Inbox Score
                </CardTitle>
                <CardDescription>
                  A one-glance check of the deliverability basics for your account.
                  Updates live as you edit the fields below.
                </CardDescription>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right leading-tight">
                  <div
                    className={`text-3xl font-bold tabular-nums ${tier.ringClass}`}
                    data-testid="inbox-score-percent"
                  >
                    {healthQuery.isLoading ? "…" : `${percent}%`}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {passingChecks} of {totalChecks} checks passing
                  </div>
                </div>
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${tier.badgeClass}`}
                  data-testid="inbox-score-tier"
                >
                  {tier.label}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {senderUnverified && (
              <div
                className="flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-700 dark:text-red-300"
                data-testid="inbox-score-sender-unverified-banner"
              >
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span className="flex-1">
                  <strong>Sends are blocked:</strong> the configured From address
                  is not verified in Resend. Until this is fixed in MailCraft's
                  server config, no email can be delivered.
                  {senderRecheckCooldown?.reason === "rate_limit" && (
                    <span
                      className="block mt-1 text-red-800 dark:text-red-200"
                      role="status"
                      aria-live="polite"
                      data-testid="inbox-score-sender-recheck-cooldown-message"
                    >
                      Just a sec — Resend lets us recheck once every 10
                      seconds. Try again in {senderRecheckCooldown.secondsLeft}{" "}
                      second{senderRecheckCooldown.secondsLeft === 1 ? "" : "s"}.
                    </span>
                  )}
                </span>
                <RecheckSenderButton
                  className="shrink-0"
                  testId="inbox-score-sender-recheck-button"
                  onCooldownChange={setSenderRecheckCooldown}
                />
              </div>
            )}
            {senderUnknown && !healthQuery.isLoading && (
              <div
                className="flex items-start gap-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-800 dark:text-amber-200"
                data-testid="inbox-score-sender-unknown-banner"
              >
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span className="flex-1">
                  Sender verification status couldn't be confirmed.
                  {senderReason ? ` ${senderReason}` : ""}
                  {senderRecheckCooldown?.reason === "rate_limit" && (
                    <span
                      className="block mt-1 text-amber-900 dark:text-amber-100"
                      role="status"
                      aria-live="polite"
                      data-testid="inbox-score-sender-recheck-cooldown-message-unknown"
                    >
                      Just a sec — Resend lets us recheck once every 10
                      seconds. Try again in {senderRecheckCooldown.secondsLeft}{" "}
                      second{senderRecheckCooldown.secondsLeft === 1 ? "" : "s"}.
                    </span>
                  )}
                </span>
                <RecheckSenderButton
                  className="shrink-0"
                  testId="inbox-score-sender-recheck-button-unknown"
                  onCooldownChange={setSenderRecheckCooldown}
                />
              </div>
            )}
            <ul className="divide-y divide-border rounded-md border border-border overflow-hidden">
              {inboxChecks.map((check) => {
                const showAsCritical =
                  check.critical && !check.ok && !senderUnknown;
                return (
                  <li
                    key={check.key}
                    className="flex items-start gap-3 p-3"
                    data-testid={`inbox-score-check-${check.key}`}
                  >
                    {check.ok ? (
                      <CheckCircle2
                        className="w-5 h-5 shrink-0 mt-0.5 text-green-600 dark:text-green-400"
                        aria-label="passing"
                      />
                    ) : showAsCritical ? (
                      <XCircle
                        className="w-5 h-5 shrink-0 mt-0.5 text-red-600 dark:text-red-400"
                        aria-label="failing"
                      />
                    ) : (
                      <XCircle
                        className="w-5 h-5 shrink-0 mt-0.5 text-muted-foreground/60"
                        aria-label="not set"
                      />
                    )}
                    <div className="min-w-0">
                      <div
                        className={`text-sm font-medium ${
                          check.ok
                            ? "text-foreground"
                            : showAsCritical
                              ? "text-red-700 dark:text-red-300"
                              : "text-foreground"
                        }`}
                      >
                        {check.label}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {check.helper}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>

        <DeliverabilityTips />

        {/* Per-user "My sending defaults" — always editable, even when the
            brand-wide settings below are admin-managed. Pre-fills the
            From name + alias on the compose page for this user only. */}
        <MySendingDefaultsCard />

        {/* Per-user Gmail integration preferences (auto-mark-read on view,
            etc.). Independent of the admin-managed brand settings. */}
        <GmailPreferencesCard />

        <WebhookPreferencesCard />

        {isManaged && (
          <Card
            data-testid="brand-managed-banner"
            className="border-amber-500/40 bg-amber-50/60 dark:bg-amber-500/10"
          >
            <CardHeader className="flex flex-row items-start gap-3 space-y-0">
              <Lock className="w-5 h-5 mt-0.5 text-amber-600 dark:text-amber-400 shrink-0" />
              <div className="min-w-0 space-y-1">
                <CardTitle className="text-base">
                  Brand managed by your administrator
                </CardTitle>
                <CardDescription>
                  {managedByEmail
                    ? `These brand settings are set centrally by ${managedByEmail} and apply to every user in this workspace. You can review them here, but changes can only be made by your administrator.`
                    : "These brand settings are set centrally by your workspace administrator and apply to every user. You can review them here, but changes can only be made by your administrator."}
                </CardDescription>
              </div>
            </CardHeader>
          </Card>
        )}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <fieldset
              disabled={isManaged}
              className="contents disabled:cursor-not-allowed"
              aria-disabled={isManaged}
            >

            <Card id="sender-card">
              <CardHeader>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <CardTitle className="text-lg flex items-center gap-2"><AtSign className="w-5 h-5" /> Sender</CardTitle>
                    <CardDescription>
                      Every email is sent from MailCraft's verified shared sender address. You only choose the From name your recipients see.
                    </CardDescription>
                  </div>
                  <SenderStatusIndicator
                    senderConfig={senderConfig}
                    testIdSuffix="-brand"
                  />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField control={form.control} name="fromName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>From Name</FormLabel>
                    <FormControl><Input placeholder="Your Brand" {...field} value={field.value || ""} /></FormControl>
                    <FormDescription>Friendly display name shown before the address.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="fromLocalPart" render={({ field }) => (
                  <FormItem>
                    <FormLabel>From Address</FormLabel>
                    <FormControl>
                      <div className="flex items-stretch rounded-md border border-input focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background overflow-hidden">
                        <Input
                          {...field}
                          value={field.value || ""}
                          onChange={(e) =>
                            field.onChange(e.target.value.toLowerCase())
                          }
                          placeholder={defaultLocalPart}
                          className="border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 font-mono"
                          data-testid="input-from-local-part"
                          maxLength={64}
                          autoCapitalize="none"
                          autoCorrect="off"
                          spellCheck={false}
                        />
                        <span
                          className="flex items-center px-3 bg-muted/40 text-sm text-muted-foreground font-mono border-l border-input"
                          data-testid="from-domain-suffix"
                        >
                          @{fromDomain}
                        </span>
                      </div>
                    </FormControl>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <span className="text-xs text-muted-foreground self-center mr-1">
                        Quick pick:
                      </span>
                      {FROM_PRESETS.map((preset) => {
                        const active = (field.value || "").toLowerCase() === preset;
                        return (
                          <button
                            key={preset}
                            type="button"
                            onClick={() =>
                              field.onChange(active ? "" : preset)
                            }
                            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                              active
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-muted/40 text-foreground border-border hover:bg-muted"
                            }`}
                            data-testid={`from-preset-${preset}`}
                          >
                            {preset}
                          </button>
                        );
                      })}
                      {field.value ? (
                        <button
                          type="button"
                          onClick={() => field.onChange("")}
                          className="px-2.5 py-1 rounded-full text-xs border border-dashed border-border text-muted-foreground hover:bg-muted"
                          data-testid="from-preset-clear"
                        >
                          Clear
                        </button>
                      ) : null}
                    </div>
                    <FormDescription>
                      The part before "@". Domain is fixed to your verified
                      shared sender. Lowercase letters, digits, ".", "_" or "-".
                      Leave empty to use the default ({defaultLocalPart || "shared"}).
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />
                <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
                  <span className="text-muted-foreground">Preview:&nbsp;</span>
                  <span className="font-mono" data-testid="from-preview">{previewFrom}</span>
                </div>
              </CardContent>
            </Card>

            <DnsRecordsCard />

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">General Identity</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="companyName" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name</FormLabel>
                      <FormControl><Input {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="websiteUrl" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website URL</FormLabel>
                      <FormControl><Input placeholder="https://" {...field} value={field.value || ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>

                <FormField control={form.control} name="logoUrl" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Logo URL</FormLabel>
                    <FormControl><Input placeholder="https://..." {...field} value={field.value || ""} /></FormControl>
                    <FormDescription>Absolute URL to your hosted logo image.</FormDescription>
                    <FormMessage />
                    <div className="pt-2 space-y-3">
                      <p className="text-xs text-muted-foreground">Or pick a sample logo:</p>
                      {SAMPLE_LOGO_ROWS.map((row) => (
                        <div key={row.format} className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {row.label}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {row.helper}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {SAMPLE_LOGOS.map((sample) => {
                              const url = row.format === "svg" ? sample.svgUrl : sample.pngUrl;
                              const isActive = (field.value || "") === url;
                              return (
                                <button
                                  key={`${sample.name}-${row.format}`}
                                  type="button"
                                  onClick={() =>
                                    field.onChange(isActive ? "" : url)
                                  }
                                  aria-pressed={isActive}
                                  className={`flex h-12 w-28 items-center justify-center rounded-md border bg-white px-3 transition-colors hover-elevate ${
                                    isActive
                                      ? "border-primary ring-2 ring-primary/30"
                                      : "border-border"
                                  }`}
                                  title={`Use ${sample.name} ${row.label} sample logo`}
                                  data-testid={`button-sample-logo-${sample.name.toLowerCase()}-${row.format}`}
                                >
                                  <img
                                    src={url}
                                    alt={`${sample.name} logo (${row.label})`}
                                    className="max-h-8 max-w-full object-contain"
                                    loading="lazy"
                                  />
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </FormItem>
                )} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><Palette className="w-5 h-5"/> Colors</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-8">
                <FormField control={form.control} name="primaryColor" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Primary Color</FormLabel>
                    <div className="flex gap-3">
                      <FormControl>
                        <Input type="color" className="w-12 h-10 p-1 cursor-pointer" {...field} />
                      </FormControl>
                      <FormControl>
                        <Input className="font-mono uppercase flex-1" {...field} />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="accentColor" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Background/Accent Color</FormLabel>
                    <div className="flex gap-3">
                      <FormControl>
                        <Input type="color" className="w-12 h-10 p-1 cursor-pointer" {...field} />
                      </FormControl>
                      <FormControl>
                        <Input className="font-mono uppercase flex-1" {...field} />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Footer Information</CardTitle>
                <CardDescription>This information is often legally required in commercial emails.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="supportEmail" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Support Email</FormLabel>
                      <FormControl><Input placeholder="support@..." {...field} value={field.value || ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="unsubscribeUrl" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unsubscribe URL</FormLabel>
                      <FormControl><Input placeholder="https://..." {...field} value={field.value || ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>

                <FormField control={form.control} name="addressLine" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Physical Address</FormLabel>
                    <FormControl><Input placeholder="123 Main St, City, Country" {...field} value={field.value || ""} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField control={form.control} name="footerNote" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Footer Note</FormLabel>
                    <FormControl><Textarea rows={2} placeholder="You are receiving this because..." {...field} value={field.value || ""} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Engagement Tracking</CardTitle>
                <CardDescription>
                  Track who opens your emails and clicks links. Tracking is off by default for privacy. When enabled, opens and clicks are recorded in each email's log and rolled up on the dashboard.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField control={form.control} name="trackOpens" render={({ field }) => (
                  <FormItem className="flex items-center justify-between gap-4 rounded-md border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Track opens</FormLabel>
                      <FormDescription>
                        Inserts a tiny invisible image into each email so we can record when it's opened. May be blocked by privacy-conscious mail clients (Apple Mail Privacy Protection inflates open rates).
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={!!field.value}
                        onCheckedChange={field.onChange}
                        data-testid="switch-track-opens"
                      />
                    </FormControl>
                  </FormItem>
                )} />
                <FormField control={form.control} name="trackClicks" render={({ field }) => (
                  <FormItem className="flex items-center justify-between gap-4 rounded-md border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Track clicks</FormLabel>
                      <FormDescription>
                        Rewrites links in your emails so we can record clicks before forwarding to the original URL. Recipients see a slightly longer URL on hover.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={!!field.value}
                        onCheckedChange={field.onChange}
                        data-testid="switch-track-clicks"
                      />
                    </FormControl>
                  </FormItem>
                )} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">DMARC Policy</CardTitle>
                <CardDescription>
                  Controls the synthesized DMARC TXT record on the DNS Records card. Start with <span className="font-mono">none</span> to monitor your reports without affecting delivery, then tighten to <span className="font-mono">quarantine</span> or <span className="font-mono">reject</span> as your authentication looks clean.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField control={form.control} name="dmarcPolicy" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Policy</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isManaged}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-dmarc-policy">
                          <SelectValue placeholder="Select a DMARC policy" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {DMARC_POLICY_INPUT_VALUES.map((v) => (
                          <SelectItem key={v} value={v} data-testid={`select-dmarc-policy-${v}`}>
                            {DMARC_POLICY_LABELS[v]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Don't jump straight to <span className="font-mono">reject</span> without first watching your aggregate reports — misconfigured legitimate mail will start being dropped.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField control={form.control} name="dmarcReportingEmail" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Aggregate report email <span className="font-normal text-muted-foreground">(optional)</span></FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="dmarc-reports@yourdomain.com"
                        autoComplete="off"
                        {...field}
                        value={field.value || ""}
                        data-testid="input-dmarc-reporting-email"
                      />
                    </FormControl>
                    <FormDescription>
                      Receivers like Gmail and Yahoo email a daily XML report here. Leave blank if you don't want to collect reports — DMARC still works either way.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />
              </CardContent>
            </Card>

            {!isManaged && (
              <div className="flex justify-end">
                <Button type="submit" disabled={updateMutation.isPending} size="lg">
                  {updateMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save Brand Settings
                </Button>
              </div>
            )}

            </fieldset>
          </form>
        </Form>

        {me?.isAdmin === true && <InboundMailboxesCard />}
        {me?.isAdmin === true && <TrustedSendersCard />}
      </div>
    </div>
  );
}

type MailboxFormState = { localPart: string; displayName: string };
const EMPTY_MAILBOX_FORM: MailboxFormState = { localPart: "", displayName: "" };

/**
 * Format an ISO timestamp as a short, human-friendly relative string
 * ("just now", "3m ago", "2h ago", "Apr 30"). Designed for the inline
 * diagnostics strip — keeps the UI readable without a heavy date lib.
 */
function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "unknown";
  const diff = Math.max(0, Date.now() - then);
  const sec = Math.round(diff / 1000);
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 14) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Process-level diagnostics card rendered above the configured
 * mailboxes list. Only shows when there is something interesting to
 * say (Basic Auth env missing, or recent auth/recipient failures); a
 * silent healthy install gets no extra UI noise.
 */
function WebhookDiagnosticsBanner({
  diagnostics,
}: {
  diagnostics: InboundWebhookDiagnostics | undefined;
}) {
  if (!diagnostics) return null;
  const {
    basicAuthConfigured,
    lastSignatureFailureAt,
    signatureFailureCount,
    lastUnknownRecipientAt,
    lastUnknownRecipientAddress,
    unknownRecipientCount,
  } = diagnostics;

  const hasSignatureFailures = signatureFailureCount > 0;
  const hasUnknownRecipients = unknownRecipientCount > 0;
  if (
    basicAuthConfigured &&
    !hasSignatureFailures &&
    !hasUnknownRecipients
  ) {
    return null;
  }

  return (
    <div
      className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-900 dark:text-amber-100 space-y-2"
      data-testid="inbound-webhook-diagnostics"
    >
      {!basicAuthConfigured && (
        <div
          className="flex items-start gap-2"
          data-testid="inbound-webhook-diagnostic-secret-missing"
        >
          <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
          <div>
            <strong>Webhook Basic Auth not configured.</strong> Set{" "}
            <code className="font-mono">SENDGRID_INBOUND_BASIC_AUTH_USER</code>{" "}
            and{" "}
            <code className="font-mono">
              SENDGRID_INBOUND_BASIC_AUTH_PASSWORD
            </code>{" "}
            on the server so the inbound provider's deliveries can be
            authenticated. Until both are set, every inbound delivery
            will be rejected with a 503.
          </div>
        </div>
      )}
      {hasSignatureFailures && (
        <div
          className="flex items-start gap-2"
          data-testid="inbound-webhook-diagnostic-signature-failures"
        >
          <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong>
              {signatureFailureCount} webhook authentication failure
              {signatureFailureCount === 1 ? "" : "s"}
            </strong>{" "}
            since this server started — last {timeAgo(lastSignatureFailureAt)}.
            Usually means the username / password in{" "}
            <code className="font-mono">
              SENDGRID_INBOUND_BASIC_AUTH_USER
            </code>{" "}
            /{" "}
            <code className="font-mono">
              SENDGRID_INBOUND_BASIC_AUTH_PASSWORD
            </code>{" "}
            don't match the credentials set on the inbound provider's
            webhook configuration.
          </div>
        </div>
      )}
      {hasUnknownRecipients && (
        <div
          className="flex items-start gap-2"
          data-testid="inbound-webhook-diagnostic-unknown-recipient"
        >
          <CircleHelp className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong>
              {unknownRecipientCount} delivery
              {unknownRecipientCount === 1 ? "" : "s"} dropped — recipient
              not configured.
            </strong>{" "}
            Last hit{" "}
            {lastUnknownRecipientAddress ? (
              <code className="font-mono">{lastUnknownRecipientAddress}</code>
            ) : (
              "an address"
            )}{" "}
            {timeAgo(lastUnknownRecipientAt)}. Add this address as a mailbox
            below, or update the inbound provider's routing.
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Per-mailbox diagnostic strip (the small line under the address row).
 * Surfaces the most recent webhook attempt for THIS mailbox so the
 * operator knows whether the inbound provider has tried, succeeded, or
 * failed for it specifically.
 */
function MailboxDiagnosticStrip({ mailbox }: { mailbox: InboundMailbox }) {
  const attemptAt = mailbox.lastWebhookAttemptAt;
  const outcome = mailbox.lastWebhookOutcome;
  const error = mailbox.lastWebhookError;

  // Don't render for mailboxes that are already active AND have a
  // clean recent attempt — there's nothing useful to say.
  if (mailbox.status === "active" && outcome === "accepted" && !error) {
    return null;
  }

  if (!attemptAt) {
    return (
      <div
        className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1"
        data-testid={`mailbox-diagnostic-${mailbox.address}`}
      >
        <Clock className="w-3 h-3" />
        <span>
          The inbound provider has not attempted delivery to this address yet.
        </span>
      </div>
    );
  }

  const outcomeLabel =
    outcome === "accepted"
      ? "delivered"
      : outcome === "duplicate"
        ? "duplicate (already received)"
        : outcome === "persist_failed"
          ? "failed to persist"
          : (outcome ?? "unknown");
  const isFailure = outcome === "persist_failed";

  return (
    <div
      className={`text-xs mt-1 flex items-start gap-1.5 ${
        isFailure ? "text-red-700 dark:text-red-300" : "text-muted-foreground"
      }`}
      data-testid={`mailbox-diagnostic-${mailbox.address}`}
    >
      <Clock className="w-3 h-3 mt-0.5 shrink-0" />
      <span>
        Last attempt {timeAgo(attemptAt)} — {outcomeLabel}
        {error ? <>: <span className="font-mono">{error}</span></> : null}
      </span>
    </div>
  );
}

function InboundMailboxesCard() {
  const queryClient = useQueryClient();
  // Two parallel queries: the default (active+pending+failed) feeds
  // the main list, and the includeDisabled=true variant feeds the
  // "Show removed" disclosure when the user opens it. Splitting the
  // queries keeps the badge counts on the main list snappy and only
  // pays the cost of fetching disabled rows when they're actually
  // requested.
  const inboundMailboxesQuery = useListInboundMailboxes();
  const [showRemoved, setShowRemoved] = useState(false);
  const removedMailboxesQuery = useListInboundMailboxes(
    { includeDisabled: true },
    {
      query: {
        queryKey: getListInboundMailboxesQueryKey({ includeDisabled: true }),
        // Only hit the API once the user clicks the disclosure;
        // before then we don't know (or care) how many removed
        // mailboxes exist. Once enabled, react-query keeps the
        // result around so flipping the disclosure is instant.
        enabled: showRemoved,
      },
    },
  );
  const createMutation = useCreateInboundMailbox();
  const updateMutation = useUpdateInboundMailbox();
  const deleteMutation = useDeleteInboundMailbox();
  const restoreMutation = useRestoreInboundMailbox();
  const purgeMutation = useAdminPurgeInboundMailbox();
  const testWebhookMutation = useTestInboundWebhook();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<InboundMailbox | null>(null);
  const [form, setForm] = useState<MailboxFormState>(EMPTY_MAILBOX_FORM);
  const [confirmDelete, setConfirmDelete] = useState<InboundMailbox | null>(null);
  // Result of the most recent self-test, keyed on mailbox id so the
  // outcome stays visible inline next to the row that triggered it.
  const [testResult, setTestResult] = useState<
    { mailboxId: number; result: TestInboundWebhookResult | null; error: string | null } | null
  >(null);
  const [testingMailboxId, setTestingMailboxId] = useState<number | null>(null);
  const [confirmPurge, setConfirmPurge] = useState<InboundMailbox | null>(null);

  async function runWebhookTest(mailbox: InboundMailbox) {
    setTestingMailboxId(mailbox.id);
    setTestResult({ mailboxId: mailbox.id, result: null, error: null });
    try {
      const result = await testWebhookMutation.mutateAsync({
        data: { mailboxId: mailbox.id },
      });
      setTestResult({ mailboxId: mailbox.id, result, error: null });
      if (result.ok) {
        toast.success(
          `Test event delivered (HTTP ${result.status}) — your webhook secret is wired up correctly.`,
        );
        // Refresh the mailbox list so the just-recorded attempt
        // shows up in the diagnostic strip immediately.
        queryClient.invalidateQueries({
          queryKey: getListInboundMailboxesQueryKey(),
        });
      } else {
        toast.error(
          `Webhook returned HTTP ${result.status} — check the details inline.`,
        );
      }
    } catch (err) {
      const msg = apiErrorMessage(err, "Failed to run webhook test");
      setTestResult({ mailboxId: mailbox.id, result: null, error: msg });
      toast.error(msg);
    } finally {
      setTestingMailboxId(null);
    }
  }

  const invalidate = () => {
    // Both list variants need to be refreshed after any mutation —
    // a restore moves a row out of the disabled-only list AND into
    // the main one, and a delete moves it the other way.
    queryClient.invalidateQueries({
      queryKey: getListInboundMailboxesQueryKey(),
    });
    queryClient.invalidateQueries({
      queryKey: getListInboundMailboxesQueryKey({ includeDisabled: true }),
    });
  };

  const removedMailboxes = (
    removedMailboxesQuery.data?.mailboxes ?? []
  ).filter((m) => m.status === "disabled");

  async function purgeMailbox() {
    if (!confirmPurge) return;
    try {
      await purgeMutation.mutateAsync({ id: confirmPurge.id });
      toast.success(`Mailbox ${confirmPurge.address} permanently deleted`);
      setConfirmPurge(null);
      invalidate();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to permanently delete mailbox"));
    }
  }

  async function restoreMailbox(m: InboundMailbox) {
    try {
      await restoreMutation.mutateAsync({ id: m.id });
      toast.success(`Mailbox ${m.address} restored`);
      invalidate();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to restore mailbox"));
    }
  }

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_MAILBOX_FORM);
    setOpen(true);
  }

  function openEdit(m: InboundMailbox) {
    setEditing(m);
    // Local part is read-only when editing — only the display name is
    // editable. Pre-fill the local-part field so the user has context
    // even though we render it disabled.
    const local = m.address.includes("@")
      ? m.address.slice(0, m.address.lastIndexOf("@"))
      : m.address;
    setForm({ localPart: local, displayName: m.displayName ?? "" });
    setOpen(true);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const localPart = form.localPart.trim().toLowerCase();
    const displayName = form.displayName.trim();

    if (editing) {
      if (displayName.length === 0) {
        toast.error("Display name is required");
        return;
      }
      if (displayName.length > DISPLAY_NAME_MAX_LENGTH) {
        toast.error(`Display name must be ${DISPLAY_NAME_MAX_LENGTH} characters or fewer`);
        return;
      }
      try {
        await updateMutation.mutateAsync({
          id: editing.id,
          data: { displayName },
        });
        toast.success("Mailbox updated");
        setOpen(false);
        invalidate();
      } catch (err) {
        toast.error(apiErrorMessage(err, "Failed to update mailbox"));
      }
      return;
    }

    if (localPart.length === 0) {
      toast.error("Mailbox local part is required");
      return;
    }
    if (localPart.length > LOCAL_PART_MAX_LENGTH) {
      toast.error(`Local part must be ${LOCAL_PART_MAX_LENGTH} characters or fewer`);
      return;
    }
    if (!LOCAL_PART_PATTERN.test(localPart)) {
      toast.error(
        "Only letters, digits, dots, underscores, plus, and hyphens are allowed",
      );
      return;
    }
    if (displayName.length > DISPLAY_NAME_MAX_LENGTH) {
      toast.error(`Display name must be ${DISPLAY_NAME_MAX_LENGTH} characters or fewer`);
      return;
    }

    try {
      await createMutation.mutateAsync({
        data: {
          localPart,
          ...(displayName.length > 0 ? { displayName } : {}),
        },
      });
      toast.success(`Mailbox ${localPart}@${inboundMailboxesQuery.data?.domain ?? ""} added`);
      setOpen(false);
      invalidate();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to add mailbox"));
    }
  }

  async function confirmDeleteMailbox() {
    if (!confirmDelete) return;
    try {
      await deleteMutation.mutateAsync({ id: confirmDelete.id });
      toast.success(`Mailbox ${confirmDelete.address} removed`);
      setConfirmDelete(null);
      invalidate();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to remove mailbox"));
    }
  }

  const submitting = createMutation.isPending || updateMutation.isPending;
  const domain = inboundMailboxesQuery.data?.domain ?? "—";

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <CardTitle className="text-lg flex items-center gap-2">
                <InboxIcon className="w-5 h-5" />
                Inbound Mailboxes
              </CardTitle>
              <CardDescription>
                Receive replies and customer emails on addresses you control,
                then triage them in the in-app Inbox.
              </CardDescription>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={openCreate}
              data-testid="button-add-inbound-mailbox"
              className="shrink-0"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add mailbox
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border border-border p-4 space-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">Inbound domain:</span>{" "}
              <span className="font-mono font-medium" data-testid="inbound-domain">
                {domain}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Set the env var <code className="font-mono">MAILCRAFT_INBOUND_DOMAIN</code>{" "}
              to override (defaults to the host portion of your sender address).
            </p>
          </div>

          <WebhookDiagnosticsBanner
            diagnostics={inboundMailboxesQuery.data?.diagnostics}
          />

          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Configured mailboxes</h3>
            {inboundMailboxesQuery.isLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            ) : (inboundMailboxesQuery.data?.mailboxes ?? []).length === 0 ? (
              <div
                className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground"
                data-testid="brand-mailboxes-empty"
              >
                No mailboxes configured yet. Click "Add mailbox" to create one.
              </div>
            ) : (
              <ul className="divide-y divide-border rounded-md border border-border">
                {(inboundMailboxesQuery.data?.mailboxes ?? []).map((m) => {
                  const lastTest =
                    testResult && testResult.mailboxId === m.id
                      ? testResult
                      : null;
                  return (
                    <li
                      key={m.id}
                      className="px-4 py-3 text-sm space-y-2"
                      data-testid={`brand-mailbox-${m.address}`}
                    >
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0 flex-wrap">
                          <span className="font-mono">{m.address}</span>
                          {m.displayName && (
                            <span className="text-xs text-muted-foreground">
                              ({m.displayName})
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {m.unreadCount > 0 && (
                            <Badge variant="secondary">
                              {m.unreadCount} unread
                            </Badge>
                          )}
                          {m.status === "active" ? (
                            <Badge
                              variant="outline"
                              className="text-emerald-600 border-emerald-600/30 bg-emerald-500/10"
                            >
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              active
                            </Badge>
                          ) : m.status === "pending" ? (
                            <Badge
                              variant="outline"
                              className="text-amber-700 border-amber-500/30 bg-amber-500/10"
                            >
                              <Clock className="w-3 h-3 mr-1" />
                              pending
                            </Badge>
                          ) : (
                            <Badge variant="destructive">{m.status}</Badge>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => runWebhookTest(m)}
                                disabled={testingMailboxId === m.id}
                                aria-label={`Send test webhook to ${m.address}`}
                                data-testid={`button-test-webhook-${m.address}`}
                              >
                                {testingMailboxId === m.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Send className="h-4 w-4" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              Send a self-signed test event to verify the
                              inbound webhook is wired up.
                            </TooltipContent>
                          </Tooltip>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => openEdit(m)}
                            aria-label={`Edit ${m.address}`}
                            data-testid={`button-edit-mailbox-${m.address}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => setConfirmDelete(m)}
                            aria-label={`Remove ${m.address}`}
                            data-testid={`button-delete-mailbox-${m.address}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <MailboxDiagnosticStrip mailbox={m} />
                      {lastTest && (
                        <div
                          className={`text-xs rounded-md border px-3 py-2 ${
                            lastTest.error || (lastTest.result && !lastTest.result.ok)
                              ? "border-red-500/30 bg-red-500/5 text-red-700 dark:text-red-300"
                              : "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300"
                          }`}
                          data-testid={`mailbox-test-result-${m.address}`}
                        >
                          {lastTest.error ? (
                            <span>Test failed: {lastTest.error}</span>
                          ) : lastTest.result ? (
                            <span>
                              Webhook responded with HTTP{" "}
                              <span className="font-mono">
                                {lastTest.result.status}
                              </span>
                              {lastTest.result.body
                                ? <>: <span className="font-mono">{lastTest.result.body}</span></>
                                : null}
                            </span>
                          ) : null}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <RemovedMailboxesDisclosure
            open={showRemoved}
            onToggle={() => setShowRemoved((v) => !v)}
            isLoading={
              showRemoved &&
              (removedMailboxesQuery.isLoading ||
                removedMailboxesQuery.isFetching)
            }
            mailboxes={removedMailboxes}
            onRestore={restoreMailbox}
            restoringId={
              restoreMutation.isPending
                ? (restoreMutation.variables?.id ?? null)
                : null
            }
            onPurge={setConfirmPurge}
            purgingId={
              purgeMutation.isPending
                ? (purgeMutation.variables?.id ?? null)
                : null
            }
          />

          <div className="rounded-md border border-border bg-muted/30 p-4 text-xs text-muted-foreground space-y-2">
            <p className="font-medium text-foreground">
              Provider setup (SendGrid Inbound Parse)
            </p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>
                Add MX records on your inbound domain pointing to{" "}
                <code className="font-mono">mx.sendgrid.net</code> with
                priorities <code className="font-mono">10</code> and{" "}
                <code className="font-mono">20</code>. Wait for DNS to
                propagate before adding the host on SendGrid.
              </li>
              <li>
                In SendGrid, open <em>Settings → Inbound Parse → Add
                Host &amp; URL</em>. Set the host to your inbound domain
                and the destination URL to{" "}
                <code className="font-mono">
                  {(typeof window !== "undefined" ? window.location.origin : "")}
                  {import.meta.env.BASE_URL.replace(/\/$/, "")}
                  /api/inbound/sendgrid
                </code>
                .
              </li>
              <li>
                Set HTTP Basic Auth credentials on the SendGrid host
                entry, then mirror the same username and password into
                the env vars{" "}
                <code className="font-mono">
                  SENDGRID_INBOUND_BASIC_AUTH_USER
                </code>{" "}
                and{" "}
                <code className="font-mono">
                  SENDGRID_INBOUND_BASIC_AUTH_PASSWORD
                </code>{" "}
                on this server. The webhook will reject (401) any
                request whose decoded{" "}
                <code className="font-mono">Authorization: Basic …</code>{" "}
                header doesn't match.
              </li>
              <li>
                Send a test message to one of your mailboxes. Its status
                will flip from{" "}
                <span className="font-medium">pending</span> to{" "}
                <span className="font-medium">active</span> on the first
                successful delivery.
              </li>
            </ol>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit mailbox" : "Add inbound mailbox"}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? "Update the friendly display name for this mailbox. The address itself can't be changed once messages have been received against it."
                : `New mailboxes are created on your configured inbound domain (${domain}). Provide just the local-part — the part that goes before the "@".`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mailbox-local-part">Local part</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="mailbox-local-part"
                  value={form.localPart}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, localPart: e.target.value }))
                  }
                  placeholder="billing"
                  autoFocus={!editing}
                  disabled={!!editing}
                  maxLength={LOCAL_PART_MAX_LENGTH}
                  className="font-mono"
                  data-testid="input-mailbox-local-part"
                />
                <span className="text-sm text-muted-foreground font-mono whitespace-nowrap">
                  @{domain}
                </span>
              </div>
              {!editing && (
                <p className="text-xs text-muted-foreground">
                  Letters, digits, and{" "}
                  <code className="font-mono">. _ + -</code> only.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="mailbox-display-name">
                Display name {editing ? "" : "(optional)"}
              </Label>
              <Input
                id="mailbox-display-name"
                value={form.displayName}
                onChange={(e) =>
                  setForm((f) => ({ ...f, displayName: e.target.value }))
                }
                placeholder={editing ? "" : "Billing"}
                maxLength={DISPLAY_NAME_MAX_LENGTH}
                autoFocus={!!editing}
                data-testid="input-mailbox-display-name"
              />
              <p className="text-xs text-muted-foreground">
                Friendly label shown next to the address in the inbox.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="button-save-mailbox"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editing ? "Save changes" : "Add mailbox"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this mailbox?</AlertDialogTitle>
            <AlertDialogDescription>
              New mail addressed to{" "}
              <code className="font-mono">{confirmDelete?.address}</code>{" "}
              will be rejected. Messages already received here stay readable
              in the inbox — only future deliveries stop. You can re-add the
              same address later to re-enable it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteMailbox}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete-mailbox"
            >
              {deleteMutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!confirmPurge}
        onOpenChange={(o) => {
          if (!o && !purgeMutation.isPending) setConfirmPurge(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete this mailbox?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{" "}
              <code className="font-mono">{confirmPurge?.address}</code>
              {confirmPurge && confirmPurge.messageCount > 0 ? (
                <>
                  {" "}and its{" "}
                  <strong>
                    {confirmPurge.messageCount} message
                    {confirmPurge.messageCount === 1 ? "" : "s"}
                  </strong>
                </>
              ) : null}
              . This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={purgeMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={purgeMailbox}
              disabled={purgeMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-purge-mailbox"
            >
              {purgeMutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Permanently delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function RemovedMailboxesDisclosure({
  open,
  onToggle,
  isLoading,
  mailboxes,
  onRestore,
  restoringId,
  onPurge,
  purgingId,
}: {
  open: boolean;
  onToggle: () => void;
  isLoading: boolean;
  mailboxes: InboundMailbox[];
  onRestore: (m: InboundMailbox) => void;
  restoringId: number | null;
  onPurge: (m: InboundMailbox) => void;
  purgingId: number | null;
}) {
  // The disclosure header is rendered as a button so the entire row
  // is clickable / focusable / keyboard-activatable. We deliberately
  // don't show "(N)" while the disclosure is closed and the count
  // hasn't been fetched yet — pre-fetching every removed mailbox on
  // page load just to render a number would waste a query for the
  // typical user who never opens this panel.
  const ChevronIcon = open ? ChevronDown : ChevronRight;
  const haveCount = open && !isLoading;
  return (
    <div
      className="rounded-md border border-border"
      data-testid="brand-removed-mailboxes-disclosure"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls="brand-removed-mailboxes-list"
        className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-muted/40 rounded-md"
        data-testid="button-toggle-removed-mailboxes"
      >
        <span className="flex items-center gap-2">
          <ChevronIcon className="h-4 w-4 text-muted-foreground" />
          <Archive className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">Show removed mailboxes</span>
          {haveCount && (
            <span
              className="text-xs text-muted-foreground"
              data-testid="removed-mailboxes-count"
            >
              ({mailboxes.length})
            </span>
          )}
        </span>
        {open && isLoading && (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </button>
      {open && (
        <div
          id="brand-removed-mailboxes-list"
          className="border-t border-border p-3 space-y-2"
        >
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : mailboxes.length === 0 ? (
            <div
              className="rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground"
              data-testid="brand-removed-mailboxes-empty"
            >
              No removed mailboxes. Mailboxes you delete from the list
              above appear here so you can put them back later.
            </div>
          ) : (
            <ul className="divide-y divide-border rounded-md border border-border">
              {mailboxes.map((m) => {
                const restoring = restoringId === m.id;
                const purging = purgingId === m.id;
                return (
                  <li
                    key={m.id}
                    className="flex items-center justify-between px-4 py-3 text-sm gap-3"
                    data-testid={`brand-removed-mailbox-${m.address}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-wrap">
                      <span className="font-mono text-muted-foreground line-through">
                        {m.address}
                      </span>
                      {m.displayName && (
                        <span className="text-xs text-muted-foreground">
                          ({m.displayName})
                        </span>
                      )}
                      {m.disabledAt && (
                        <span
                          className="text-xs text-muted-foreground"
                          title={new Date(m.disabledAt).toLocaleString()}
                          data-testid={`removed-mailbox-disabled-at-${m.address}`}
                        >
                          removed{" "}
                          {formatDistanceToNow(new Date(m.disabledAt), {
                            addSuffix: true,
                          })}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {m.messageCount > 0 && (
                        <Badge
                          variant="secondary"
                          data-testid={`removed-mailbox-message-count-${m.address}`}
                          title="Historical messages waiting in the inbox if this mailbox is restored"
                        >
                          {m.messageCount} message
                          {m.messageCount === 1 ? "" : "s"} waiting
                        </Badge>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => onRestore(m)}
                        disabled={restoring || purging}
                        data-testid={`button-restore-mailbox-${m.address}`}
                      >
                        {restoring ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <RotateCcw className="h-4 w-4 mr-2" />
                        )}
                        Restore
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => onPurge(m)}
                        disabled={restoring || purging}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid={`button-purge-mailbox-${m.address}`}
                      >
                        {purging ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4 mr-2" />
                        )}
                        Permanently delete
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

interface TrustedSenderRowProps {
  row: TrustedSender;
  onDelete: (row: TrustedSender) => void;
  isDeleting: boolean;
}

function TrustedSenderRow({ row, onDelete, isDeleting }: TrustedSenderRowProps) {
  return (
    <li
      className="flex items-center justify-between gap-3 p-3"
      data-testid={`trusted-sender-row-${row.domain}`}
    >
      <div className="flex items-start gap-2 min-w-0">
        <BadgeCheck
          className="h-4 w-4 mt-0.5 shrink-0 text-blue-600 dark:text-blue-400"
          fill="currentColor"
          stroke="white"
          strokeWidth={2}
          aria-hidden="true"
        />
        <div className="min-w-0">
          <div className="font-mono text-sm truncate" data-testid={`trusted-sender-domain-${row.domain}`}>
            {row.domain}
          </div>
          {row.addedByEmail && (
            <div className="text-xs text-muted-foreground truncate">
              Added by {row.addedByEmail}
            </div>
          )}
        </div>
      </div>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={() => onDelete(row)}
        disabled={isDeleting}
        aria-label={`Remove ${row.domain}`}
        data-testid={`button-remove-trusted-sender-${row.domain}`}
      >
        {isDeleting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Trash2 className="h-4 w-4" />
        )}
      </Button>
    </li>
  );
}

/**
 * Admin-only "Trusted senders" card. Lets the workspace's source
 * admin extend the curated brand allow-list with their own vendor
 * domains so the small blue "Verified sender" tick appears next to
 * mail from those senders too. Validates input client-side via
 * `normalizeTrustedDomain` so obviously bad input ("not a domain",
 * pasted URLs, etc.) is rejected before a round-trip.
 */
function TrustedSendersCard() {
  const queryClient = useQueryClient();
  const listQuery = useListTrustedSenders();
  const createMutation = useCreateTrustedSender();
  const deleteMutation = useDeleteTrustedSender();

  const [input, setInput] = useState("");
  const [inputError, setInputError] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const items = listQuery.data?.items ?? [];

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    const normalized = normalizeTrustedDomain(input);
    if (!normalized) {
      setInputError(
        "Enter a valid domain like example.com — letters, digits, hyphens, separated by dots.",
      );
      return;
    }
    setInputError(null);
    try {
      await createMutation.mutateAsync({ data: { domain: normalized } });
      setInput("");
      await queryClient.invalidateQueries({
        queryKey: getListTrustedSendersQueryKey(),
      });
      toast.success(`${normalized} added to trusted senders`);
    } catch (err) {
      toast.error(
        apiErrorMessage(err, "Couldn't add that domain — please try again."),
      );
    }
  }

  async function handleDelete(row: TrustedSender) {
    setPendingDeleteId(row.id);
    try {
      await deleteMutation.mutateAsync({ id: row.id });
      await queryClient.invalidateQueries({
        queryKey: getListTrustedSendersQueryKey(),
      });
      toast.success(`${row.domain} removed`);
    } catch (err) {
      toast.error(
        apiErrorMessage(err, "Couldn't remove that domain — please try again."),
      );
    } finally {
      setPendingDeleteId(null);
    }
  }

  return (
    <Card data-testid="card-trusted-senders">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <BadgeCheck
            className="w-5 h-5 text-blue-600 dark:text-blue-400"
            fill="currentColor"
            stroke="white"
            strokeWidth={2}
            aria-hidden="true"
          />
          Trusted senders
        </CardTitle>
        <CardDescription>
          Add vendor domains your team trusts (court e-filing, freight
          carriers, payment processors, …) and a small blue "Verified
          sender" tick will show next to inbox messages from those
          domains. Built-in well-known brands like PayPal, Stripe and
          Amazon already get the badge — you only need to add what's
          specific to your team.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          onSubmit={handleAdd}
          className="flex flex-col sm:flex-row gap-2 sm:items-start"
          data-testid="trusted-sender-add-form"
        >
          <div className="flex-1 min-w-0">
            <Label htmlFor="trusted-sender-input" className="sr-only">
              Domain to trust
            </Label>
            <Input
              id="trusted-sender-input"
              type="text"
              autoComplete="off"
              spellCheck={false}
              placeholder="vendor.example.com"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                if (inputError) setInputError(null);
              }}
              disabled={createMutation.isPending}
              aria-invalid={!!inputError}
              aria-describedby={inputError ? "trusted-sender-input-error" : undefined}
              data-testid="input-trusted-sender-domain"
            />
            {inputError && (
              <p
                id="trusted-sender-input-error"
                className="mt-1 text-xs text-destructive"
                data-testid="trusted-sender-input-error"
              >
                {inputError}
              </p>
            )}
          </div>
          <Button
            type="submit"
            disabled={createMutation.isPending || input.trim() === ""}
            data-testid="button-add-trusted-sender"
          >
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Add domain
          </Button>
        </form>

        {listQuery.isLoading ? (
          <div
            className="flex items-center gap-2 text-sm text-muted-foreground"
            data-testid="trusted-senders-loading"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading trusted senders…
          </div>
        ) : items.length === 0 ? (
          <div
            className="text-sm text-muted-foreground rounded-md border border-dashed border-border p-4 text-center"
            data-testid="trusted-senders-empty"
          >
            No custom trusted senders yet. Add a vendor domain above to
            extend the badge.
          </div>
        ) : (
          <ul
            className="divide-y divide-border rounded-md border border-border overflow-hidden"
            data-testid="trusted-senders-list"
          >
            {items.map((row) => (
              <TrustedSenderRow
                key={row.id}
                row={row}
                onDelete={handleDelete}
                isDeleting={
                  pendingDeleteId === row.id && deleteMutation.isPending
                }
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
